/**
 * AI.GEN lines=1-90
 * 调度工作台全局搜索条件 Store
 */
import { create } from 'zustand';
import { useStore } from 'zustand/react';

import type { DispatchWorkbenchStoreType, GlobalSearchState } from './types';
import {
  apiDispatchCityAllArea,
  apiDispatchCityUserCityList,
} from '@/apis/city';
import type { CityInfo } from '@/apis/city/declare';
import { apiDeliveryDriverGpsInfo } from '@/apis/delivery-driver';
import { apiDispatchUserAreaPopList } from '@/apis/user-area';
import type { OrderBusinessType } from '@/components/enum';
import {
  getInitialCity,
  getInitialDateRange,
  getInitialDeliveryTagList,
  getInitialIsFillReason,
  getInitialOrderBusinessType,
  getInitialSiteId,
  getInitialTimeType,
  getInitialZoom,
} from '@/utils/dispatch-workbench-local-storage';
import {
  getCommonSearchParams as buildCommonSearchParams,
  getGlobalListParams as buildGlobalListParams,
  getOrderTypeList as buildOrderTypeList,
  getPreciseSearchParams as buildPreciseSearchParams,
} from '@/utils/dispatch-workbench-search-params';

/**
 * 全局GPS定时器引用
 */
let gpsTimerRef: NodeJS.Timeout | null = null;

/**
 * 关联订单最大数量
 */
const MAX_RELATED_ORDERS = 9;

// 导出类型供外部使用
export type { HPOrderSubOption } from './types';

const initialState: GlobalSearchState = {
  cityName: getInitialCity(),
  orderAreaIdList: undefined,
  dateRange: getInitialDateRange(),
  distanceInfoList: undefined,
  preciseSearch: undefined,
  preciseSearchType: 'orderNumber', // 默认选择订单号
  skipDistributionRefreshOnce: false,
  cityList: [],
  cityLocation: undefined,
  loadingCityList: false,
  businessDistrictsList: [],
  userAreaIdList: undefined,
  userAreaList: [],
  addedRelatedOrders: {},
  orderBusinessType: getInitialOrderBusinessType(),
  checkedList: [
    'onlyPandaDeliveryOrders',
    'groupMeal',
    'showSpecialDriverOrders',
    'farawayOrderOnly',
  ],
  deliveryTagList: getInitialDeliveryTagList(),
  zoom: getInitialZoom(),
  timeType: getInitialTimeType(),
  isFillReason: getInitialIsFillReason(),
  siteId: getInitialSiteId(),
  gpsPollingOrderSn: undefined,
  gpsPollingDriverId: undefined,
  mapDriverIconService: undefined,
  currentExpandedOrderSn: undefined,
  ordersMap: {},
  routeColors: {},
};

export const DispatchWorkbenchStore = create<DispatchWorkbenchStoreType>(
  (set, get) => ({
    ...initialState,

    setCity: (city) => {
      const state = get();
      // 城市切换时自动更新经纬度
      if (city) {
        // 保存到 localStorage
        localStorage.setItem('dispatchWorkbenchCity', city);
        const selectedCity = state.cityList.find(
          (item) => item.cityName === city,
        );
        if (selectedCity) {
          set({
            cityName: city,
            cityLocation: {
              latitude: selectedCity.latitude,
              longitude: selectedCity.longitude,
            },
            // 切换城市时清空已选择的商圈和用户区域
            orderAreaIdList: undefined,
            userAreaIdList: undefined,
          });
          // 切换城市后获取商圈列表
          get().fetchBusinessDistricts(city);
        } else {
          set({
            cityName: city,
            cityLocation: undefined,
            orderAreaIdList: undefined,
          });
        }
      } else {
        // 清空时移除 localStorage
        localStorage.removeItem('dispatchWorkbenchCity');
        set({
          cityName: undefined,
          cityLocation: undefined,
          orderAreaIdList: undefined,
          userAreaIdList: undefined,
        });
      }
    },

    setOrderAreaIdList: (areaIds) => {
      set({ orderAreaIdList: areaIds });
    },

    setUserAreaIdList: (areas) => {
      set({ userAreaIdList: areas });
    },

    setDateRange: (range) => {
      set({ dateRange: range });
      // 保存到 localStorage
      if (range && range.length === 2) {
        try {
          localStorage.setItem(
            'dispatchWorkbenchDateRange',
            JSON.stringify([range[0], range[1]]),
          );
        } catch {
          // 保存失败时静默处理
        }
      } else {
        // 清空时移除 localStorage
        localStorage.removeItem('dispatchWorkbenchDateRange');
      }
    },

    /**
     * AI.GEN lines=1-3
     * 设置距离范围（多选）
     */
    setDistanceInfoList: (range) => {
      set({ distanceInfoList: range });
    },

    setPreciseSearch: (keyword) => {
      set({ preciseSearch: keyword });
    },

    /**
     * AI.GEN lines=1-3
     * 设置精确搜索类型
     */
    setPreciseSearchType: (type) => {
      set({ preciseSearchType: type });
    },

    /**
     * AI.GEN lines=1-1
     * 设置跳过分布地图刷新标记
     */
    setSkipDistributionRefreshOnce: (skip) => {
      set({ skipDistributionRefreshOnce: skip });
    },

    setCityList: (list: CityInfo[]) => {
      set({ cityList: list });
    },

    setCityLocation: (location?: { latitude: string; longitude: string }) => {
      set({ cityLocation: location });
    },

    fetchCityList: async () => {
      set({ loadingCityList: true });
      try {
        const response = await apiDispatchCityUserCityList();
        // 接口直接返回数组
        if (Array.isArray(response) && response.length > 0) {
          set({ cityList: response });
          const state = get();
          // 如果已有选中的城市，获取商圈列表
          if (state.cityName) {
            // 验证城市是否在列表中，如果在则获取商圈列表
            const selectedCity = response.find(
              (item) => item.cityName === state.cityName,
            );
            if (selectedCity) {
              // 更新城市经纬度
              set({
                cityLocation: {
                  latitude: selectedCity.latitude,
                  longitude: selectedCity.longitude,
                },
              });
              // 获取商圈列表
              get().fetchBusinessDistricts(state.cityName);
            } else {
              // 如果城市不在列表中，清空城市选择
              set({
                cityName: undefined,
                cityLocation: undefined,
                orderAreaIdList: undefined,
              });
              localStorage.removeItem('dispatchWorkbenchCity');
            }
          }
          // 如果没有选中城市，保持为空，不自动请求数据
        } else {
          console.warn('获取城市列表失败: 响应数据格式不正确', response);
          set({ cityList: [] });
        }
      } catch (error) {
        console.error('获取城市列表失败:', error);
        set({ cityList: [] });
      } finally {
        set({ loadingCityList: false });
      }
    },

    fetchBusinessDistricts: async (cityName) => {
      if (!cityName) {
        return;
      }
      const response = await apiDispatchCityAllArea({ cityName });
      // 接口直接返回数组
      if (Array.isArray(response)) {
        set({ businessDistrictsList: response });
      } else {
        console.warn('获取商圈列表失败: 响应数据格式不正确', response);
        set({ businessDistrictsList: [] });
      }
    },

    setDistanceComplianceSwitch: (value) => {
      set({ distanceComplianceSwitch: value });
    },

    fetchUserAreaList: async (cityName, orderBusinessType) => {
      if (!cityName || !orderBusinessType) {
        set({ userAreaList: [], userAreaIdList: undefined });
        return;
      }
      try {
        const response = await apiDispatchUserAreaPopList({
          cityName,
          orderBusinessType,
        });
        set({
          userAreaList: response.list || [],
          userAreaIdList: undefined, // 切换城市或业务类型时清空用户区域选择
        });
      } catch (error) {
        console.error('获取用户区域列表失败:', error);
        set({ userAreaList: [], userAreaIdList: undefined });
      }
    },

    addRelatedOrder: (orderId, relatedOrder) => {
      const state = get();
      const currentOrders = state.addedRelatedOrders[orderId] || [];
      // 检查是否已添加
      if (currentOrders.some((item) => item.orderSn === relatedOrder.orderSn)) {
        return; // 已存在，不重复添加
      }
      // 检查数量限制
      if (currentOrders.length >= MAX_RELATED_ORDERS) {
        return; // 已达到最大数量限制
      }
      set({
        addedRelatedOrders: {
          ...state.addedRelatedOrders,
          [orderId]: [...currentOrders, relatedOrder],
        },
      });
    },

    removeRelatedOrder: (orderId, relatedOrderId) => {
      const state = get();
      const currentOrders = state.addedRelatedOrders[orderId] || [];
      set({
        addedRelatedOrders: {
          ...state.addedRelatedOrders,
          [orderId]: currentOrders.filter(
            (item) => item.orderSn !== relatedOrderId,
          ),
        },
      });
    },

    /**
     * AI.GEN lines=1-5
     * 获取通用搜索参数
     */
    getCommonSearchParams: (options?: {
      orderBusinessType?: OrderBusinessType[];
    }) => {
      return buildCommonSearchParams(get(), options);
    },

    /**
     * AI.GEN lines=1-5
     * 获取精确搜索参数
     */
    getPreciseSearchParams: () => {
      return buildPreciseSearchParams(get());
    },

    /**
     * AI.GEN lines=1-5
     * 获取全局列表参数（包含所有列表请求的全局筛选参数）
     */
    getGlobalListParams: () => {
      return buildGlobalListParams(get());
    },

    /**
     * AI.GEN lines=1-3
     * 设置刷新回调函数
     */
    setRefreshCallback: (callback) => {
      set({ refreshCallback: callback });
    },

    /**
     * AI.GEN lines=1-3
     * 执行刷新操作
     */
    handleRefresh: () => {
      const state = get();
      if (state.refreshCallback) {
        state.refreshCallback();
      }
    },

    /**
     * AI.GEN lines=1-3
     * 设置订单业务类型
     */
    setOrderBusinessType: (types) => {
      set({ orderBusinessType: types });
      localStorage.setItem('orderBusinessType', types.join(','));
    },

    /**
     * AI.GEN lines=1-3
     * 设置HP订单子选项
     */
    setCheckedList: (list) => {
      set({ checkedList: list });
    },

    /**
     * AI.GEN lines=1-3
     * 设置PF订单配送标签列表
     */
    setDeliveryTagList: (list) => {
      set({ deliveryTagList: list });
      localStorage.setItem('deliveryTagList', list.join(','));
    },

    /**
     * AI.GEN lines=1-3
     * 设置缩放值
     */
    setZoom: (zoom) => {
      localStorage.setItem('dispatchZoom', String(zoom));
      set({ zoom });
    },

    /**
     * AI.GEN lines=1-3
     * 设置时间类型
     */
    setTimeType: (type) => {
      localStorage.setItem('dispatchTimeType', String(type));
      set({ timeType: type });
    },

    /**
     * AI.GEN lines=1-3
     * 设置是否填写撤销理由
     */
    setIsFillReason: (value) => {
      localStorage.setItem('dispatchIsFillReason', String(value));
      set({ isFillReason: value });
    },

    /**
     * AI.GEN lines=1-3
     * 设置PF站点ID
     */
    setSiteId: (id) => {
      set({ siteId: id });
      if (id !== undefined) {
        localStorage.setItem('siteId', String(id));
      } else {
        localStorage.removeItem('siteId');
      }
    },

    /**
     * AI.GEN lines=1-5
     * 获取订单类型列表（从 checkedList 转换）
     */
    getOrderTypeList: () => {
      return buildOrderTypeList(get().checkedList);
    },

    /**
     * AI.GEN lines=1-5
     * 设置地图驾驶员图标服务实例
     */
    setMapDriverIconService: (service) => {
      set({ mapDriverIconService: service });
    },

    /**
     * AI.GEN lines=1-80
     * 启动GPS轮询
     */
    startGpsPolling: (orderSn, driverId) => {
      const state = get();

      // 如果已经在轮询相同的订单和骑手，不重复启动
      if (
        state.gpsPollingOrderSn === orderSn &&
        state.gpsPollingDriverId === driverId &&
        gpsTimerRef !== null
      ) {
        return;
      }

      // 清除旧的定时器
      if (gpsTimerRef) {
        clearTimeout(gpsTimerRef);
        gpsTimerRef = null;
      }

      // 设置新的轮询状态（注意：展开状态由父组件管理，这里不设置）
      set({
        gpsPollingOrderSn: orderSn,
        gpsPollingDriverId: driverId,
      });

      /**
       * AI.GEN lines=1-10
       * 设置下一次轮询定时器
       */
      const scheduleNextPolling = () => {
        const finalState = get();

        if (
          finalState.gpsPollingOrderSn &&
          finalState.gpsPollingDriverId &&
          finalState.currentExpandedOrderSn === finalState.gpsPollingOrderSn
        ) {
          gpsTimerRef = setTimeout(() => {
            fetchGpsInfo();
          }, 5000);
        } else {
          // 订单已收起或状态不匹配，停止轮询
          if (gpsTimerRef) {
            clearTimeout(gpsTimerRef);
            gpsTimerRef = null;
          }
          set({
            gpsPollingOrderSn: undefined,
            gpsPollingDriverId: undefined,
          });
        }
      };

      // 定义GPS获取函数
      const fetchGpsInfo = async () => {
        const currentState = get();

        // 检查是否仍在轮询中
        if (
          !currentState.gpsPollingOrderSn ||
          !currentState.gpsPollingDriverId
        ) {
          return;
        }

        // 检查是否有展开的订单行，且展开的订单号与轮询的订单号一致
        if (
          !currentState.currentExpandedOrderSn ||
          currentState.currentExpandedOrderSn !== currentState.gpsPollingOrderSn
        ) {
          // 没有展开的订单或订单不匹配，停止轮询
          if (gpsTimerRef) {
            clearTimeout(gpsTimerRef);
            gpsTimerRef = null;
          }
          set({
            gpsPollingOrderSn: undefined,
            gpsPollingDriverId: undefined,
          });
          return;
        }

        try {
          const response = await apiDeliveryDriverGpsInfo({
            driverIdList: [currentState.gpsPollingDriverId],
          });

          // 再次检查状态（防止异步期间状态改变）
          const latestState = get();
          if (
            !latestState.gpsPollingOrderSn ||
            !latestState.gpsPollingDriverId
          ) {
            return;
          }

          // 再次检查展开状态（防止异步期间订单被收起）
          if (
            !latestState.currentExpandedOrderSn ||
            latestState.currentExpandedOrderSn !== latestState.gpsPollingOrderSn
          ) {
            // 订单已被收起，停止轮询
            if (gpsTimerRef) {
              clearTimeout(gpsTimerRef);
              gpsTimerRef = null;
            }
            set({
              gpsPollingOrderSn: undefined,
              gpsPollingDriverId: undefined,
            });
            return;
          }

          // 更新地图图标位置
          response.forEach((gpsInfo) => {
            const lat = gpsInfo.latitude ? parseFloat(gpsInfo.latitude) : NaN;
            const lng = gpsInfo.longitude ? parseFloat(gpsInfo.longitude) : NaN;

            // 验证经纬度有效性
            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
              // 更新地图图标位置
              if (latestState.mapDriverIconService) {
                latestState.mapDriverIconService.updateDriverPosition(
                  gpsInfo.driverId,
                  {
                    latitude: lat,
                    longitude: lng,
                  },
                );
              }
            } else {
              // 无有效经纬度，移除骑手图标
              if (latestState.mapDriverIconService) {
                latestState.mapDriverIconService.removeDriver(gpsInfo.driverId);
              }
            }
          });

          // 清除旧定时器并设置下一次轮询
          if (gpsTimerRef) {
            clearTimeout(gpsTimerRef);
            gpsTimerRef = null;
          }
          scheduleNextPolling();
        } catch (error) {
          console.error('获取骑手GPS信息失败:', error);

          // 接口报错时移除骑手图标
          const errorState = get();

          if (
            errorState.mapDriverIconService &&
            errorState.gpsPollingDriverId
          ) {
            errorState.mapDriverIconService.removeDriver(
              errorState.gpsPollingDriverId,
            );
          }

          // 报错后仍继续轮询
          if (gpsTimerRef) {
            clearTimeout(gpsTimerRef);
            gpsTimerRef = null;
          }
          scheduleNextPolling();
        }
      };

      // 立即执行一次
      fetchGpsInfo();
    },

    /**
     * AI.GEN lines=1-10
     * 停止GPS轮询
     */
    stopGpsPolling: () => {
      // 清除定时器
      if (gpsTimerRef) {
        clearTimeout(gpsTimerRef);
        gpsTimerRef = null;
      }

      // 清空轮询状态
      set({
        gpsPollingOrderSn: undefined,
        gpsPollingDriverId: undefined,
      });
    },

    /**
     * AI.GEN lines=1-5
     * 设置当前展开的订单号
     */
    setCurrentExpandedOrderSn: (orderSn) => {
      set({ currentExpandedOrderSn: orderSn });

      // 如果展开的订单号与轮询的订单号不一致，停止轮询
      const state = get();
      if (state.gpsPollingOrderSn && state.gpsPollingOrderSn !== orderSn) {
        // 清除定时器
        if (gpsTimerRef) {
          clearTimeout(gpsTimerRef);
          gpsTimerRef = null;
        }
        // 清空轮询状态
        set({
          gpsPollingOrderSn: undefined,
          gpsPollingDriverId: undefined,
        });
      }
    },

    /**
     * AI.GEN lines=1-15
     * 更新订单字段（用于跨组件同步）
     */
    updateOrderField: (orderSn, field, value) => {
      set((state) => ({
        ordersMap: {
          ...state.ordersMap,
          [orderSn]: {
            ...state.ordersMap[orderSn],
            [field]: value,
          },
        },
      }));
    },

    /**
     * AI.GEN lines=1-8
     * 获取订单字段（用于跨组件同步）
     */
    getOrderField: (orderSn, field, defaultValue) => {
      const state = get();
      return state.ordersMap[orderSn]?.[field] ?? defaultValue;
    },

    /**
     * AI.GEN lines=1-5
     * 清空订单映射
     */
    clearOrdersMap: () => {
      set({ ordersMap: {} });
    },

    /**
     * AI.GEN lines=1-8
     * 设置订单路线颜色
     */
    setRouteColor: (orderSn, color) => {
      set((state) => ({
        routeColors: {
          ...state.routeColors,
          [orderSn]: color,
        },
      }));
    },

    /**
     * AI.GEN lines=1-8
     * 移除订单路线颜色
     */
    removeRouteColor: (orderSn) => {
      set((state) => {
        const newRouteColors = { ...state.routeColors };
        delete newRouteColors[orderSn];
        return { routeColors: newRouteColors };
      });
    },
  }),
);

// 重载签名
export function useDispatchWorkbenchStore(): DispatchWorkbenchStoreType;
export function useDispatchWorkbenchStore<T>(
  selector: (state: DispatchWorkbenchStoreType) => T,
): T;

// 实现签名
export function useDispatchWorkbenchStore<T>(
  selector?: (state: DispatchWorkbenchStoreType) => T,
): T | DispatchWorkbenchStoreType {
  if (!selector) {
    return useStore(DispatchWorkbenchStore);
  }
  return useStore(DispatchWorkbenchStore, selector);
}
